import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import EmptyPage from "components/common/EmptyPage";
import performRequest from "network/perform-request";
import {
  leagueCustomerFinancialChargeTemplateRoute,
  leagueCustomerFinancialChargeTemplatesRoute,
} from "network/api-routes";
import { formatFinancialAmount } from "./financial-helpers";
import {
  DESCRIPTION_MAX_LENGTH,
  amountErrorId,
  descriptionErrorId,
  descriptionHelperText,
  isValidAmount,
  moneyFieldError,
  moneyRequestErrorMessage,
} from "./money-validation";
import { leagueTournamentRelationshipId, tournamentMatchesRelationship } from "../gestion-tournament-id";
import ChargeTypeSelect from "./ChargeTypeSelect";
import GestionTable from "components/pages/Gestion/GestionTable";
import { chargeTypeLabelId, DEFAULT_EXTERNAL_CHARGE_TYPE, isKnownChargeType } from "constants/external-charge-types";

const emptyTemplateForm = {
  name: "",
  amount: "",
  type: DEFAULT_EXTERNAL_CHARGE_TYPE,
  sku: "",
  description: "",
  external_relationship_id: "",
};

const isEntryFeeType = (type) => type === DEFAULT_EXTERNAL_CHARGE_TYPE;

// Price list templates may be 0 (the backend allows it); other money forms keep requiring > 0.
const amountOptions = { allowZero: true };

const GestionFinancialPriceList = ({ currency = "", customerId, tournaments = [] }) => {
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tournamentFilter, setTournamentFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyTemplateForm);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(moneyRequestErrorMessage(error, intl), { variant: "error" });
    },
    [enqueueSnackbar, intl],
  );

  const loadTemplates = useCallback(() => {
    if (!customerId) return Promise.resolve();
    setLoading(true);
    const params = tournamentFilter ? { external_relationship_id: tournamentFilter } : {};
    return performRequest("GET", leagueCustomerFinancialChargeTemplatesRoute(customerId), params, true, user)
      .then((response) =>
        setTemplates(
          (Array.isArray(response.data) ? response.data : []).filter((template) => template.type !== "social-club-fee"),
        ),
      )
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [customerId, handleError, tournamentFilter, user]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreate = () => {
    setEditingTemplate(null);
    setForm(emptyTemplateForm);
    setDialogOpen(true);
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name || "",
      amount: String(template.amount ?? ""),
      type: template.type,
      sku: template.sku || "",
      description: template.description || "",
      external_relationship_id: template.external_relationship_id || "",
    });
    setDialogOpen(true);
  };

  const handleSaveTemplate = () => {
    if (!customerId || !canSaveTemplate) return;
    setSaving(true);
    const body = {
      name: form.name.trim(),
      amount: Number(form.amount),
      type: form.type,
      sku: form.sku.trim() || null,
      description: form.description.trim() || null,
      external_relationship_id: form.external_relationship_id || null,
    };
    const request = editingTemplate
      ? performRequest(
          "PUT",
          leagueCustomerFinancialChargeTemplateRoute(customerId, editingTemplate.id),
          { name: form.name.trim(), amount: Number(form.amount), description: form.description.trim() || null },
          true,
          user,
        )
      : performRequest("POST", leagueCustomerFinancialChargeTemplatesRoute(customerId), body, true, user);
    request
      .then(() => {
        enqueueSnackbar(
          intl.formatMessage({
            id: editingTemplate ? "gestion.financials.templateUpdated" : "gestion.financials.templateCreated",
          }),
          { variant: "success" },
        );
        setDialogOpen(false);
        setEditingTemplate(null);
        setForm(emptyTemplateForm);
        loadTemplates();
      })
      .catch((error) => {
        if (error?.response?.data?.code === "charge_template_exists") {
          enqueueSnackbar(intl.formatMessage({ id: "gestion.financials.templateExists" }), { variant: "warning" });
          return;
        }
        const fieldErrors = error?.response?.data?.errors;
        const fieldMessage = fieldErrors?.name?.[0] || fieldErrors?.sku?.[0] || fieldErrors?.external_relationship_id?.[0];
        if (error?.response?.status === 422 && fieldMessage) {
          enqueueSnackbar(fieldMessage, { variant: "error" });
          return;
        }
        handleError(error);
      })
      .finally(() => setSaving(false));
  };

  const deleteTemplate = (template) => {
    if (!customerId || !window.confirm(intl.formatMessage({ id: "gestion.financials.confirmDelete" }))) return;
    performRequest("DELETE", leagueCustomerFinancialChargeTemplateRoute(customerId, template.id), {}, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.financials.templateDeleted" }), { variant: "success" });
        loadTemplates();
      })
      .catch(handleError);
  };

  const templateCurrency = (relationshipId) =>
    (relationshipId && tournaments.find((item) => tournamentMatchesRelationship(item, relationshipId))?.currency_code) ||
    currency;

  const tournamentLabel = (template) => {
    if (template.entry_fee_role === "extra") return intl.formatMessage({ id: "gestion.financials.entryFeeExtraScope" });
    if (!template.external_relationship_id) {
      const global = intl.formatMessage({ id: "gestion.financials.globalPriceList" });
      return template.entry_fee_role === "global"
        ? `${global} · ${intl.formatMessage({ id: "gestion.financials.entryFeeDefault" })}`
        : global;
    }
    const tournament = tournaments.find((item) => tournamentMatchesRelationship(item, template.external_relationship_id));
    return tournament?.name || template.external_relationship_id;
  };

  const amountError = moneyFieldError(intl, amountErrorId(form.amount, amountOptions));
  const canSaveTemplate =
    Boolean(form.name.trim()) &&
    isValidAmount(form.amount, amountOptions) &&
    !descriptionErrorId(form.description);
  const formCurrency = templateCurrency(form.external_relationship_id);

  return (
    <Stack spacing={2}>
      <RevaText variant="body2" color="text.secondary">
        {intl.formatMessage({ id: "gestion.financials.hint" })}
      </RevaText>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <TextField
          select
          SelectProps={{ native: true }}
          size="small"
          InputLabelProps={{ shrink: true }}
          label={intl.formatMessage({ id: "gestion.financials.tournamentFilter" })}
          value={tournamentFilter}
          onChange={(event) => setTournamentFilter(event.target.value)}
          sx={{ minWidth: 220 }}
        >
          <option value="">{intl.formatMessage({ id: "gestion.financials.allTournaments" })}</option>
          {tournaments.map((tournament) => (
            <option key={tournament.revaId ?? tournament.id} value={leagueTournamentRelationshipId(tournament)}>
              {tournament.name}
            </option>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {intl.formatMessage({ id: "gestion.financials.createTemplate" })}
        </Button>
      </Stack>

      {loading ? null : templates.length === 0 ? (
        <EmptyPage text={intl.formatMessage({ id: "gestion.financials.empty" })} />
      ) : (
        <GestionTable
          headers={[
            { key: "name", title: intl.formatMessage({ id: "gestion.financials.templateName" }) },
            {
              key: "typeLabel",
              title: intl.formatMessage({ id: "gestion.financials.templateType" }),
            },
            { key: "tournament", title: intl.formatMessage({ id: "gestion.financials.tournament" }) },
            { key: "amountLabel", title: intl.formatMessage({ id: "gestion.financials.amount" }), align: "right" },
            { key: "sku", title: intl.formatMessage({ id: "gestion.financials.sku" }) },
            {
              key: "actions",
              title: intl.formatMessage({ id: "gestion.actions" }),
              align: "right",
              renderCell: (_value, template) => (
                <>
                  <Tooltip title={intl.formatMessage({ id: "gestion.financials.editTemplate" })}>
                    <IconButton
                      size="small"
                      onClick={() => openEdit(templates.find((item) => item.id === template.id) ?? template)}
                      data-testid={`edit-template-${template.id}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={intl.formatMessage({ id: "gestion.financials.deleteTemplate" })}>
                    <IconButton size="small" onClick={() => deleteTemplate(template)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
          ]}
          rows={templates.map((template) => ({
            ...template,
            typeLabel: isKnownChargeType(template.type)
              ? intl.formatMessage({ id: chargeTypeLabelId(template.type) })
              : template.type,
            tournament: tournamentLabel(template),
            amountLabel: formatFinancialAmount(template.amount, templateCurrency(template.external_relationship_id)),
            sku: template.sku || "—",
          }))}
        />
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {intl.formatMessage({ id: editingTemplate ? "gestion.financials.editTemplate" : "gestion.financials.createTemplate" })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              SelectProps={{ native: true }}
              fullWidth
              disabled={Boolean(editingTemplate)}
              InputLabelProps={{ shrink: true }}
              label={intl.formatMessage({ id: "gestion.financials.tournament" })}
              value={form.external_relationship_id}
              onChange={(event) => setForm((prev) => ({ ...prev, external_relationship_id: event.target.value }))}
            >
              <option value="">{intl.formatMessage({ id: "gestion.financials.globalPriceList" })}</option>
              {tournaments.map((tournament) => (
                <option key={tournament.revaId ?? tournament.id} value={leagueTournamentRelationshipId(tournament)}>
                  {tournament.name}
                </option>
              ))}
            </TextField>
            <TextField
              required
              fullWidth
              disabled={Boolean(editingTemplate) && isEntryFeeType(form.type) && editingTemplate.entry_fee_role !== "extra"}
              label={intl.formatMessage({ id: "gestion.financials.templateName" })}
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
            />
            <TextField
              required
              fullWidth
              type="number"
              label={`${intl.formatMessage({ id: "gestion.financials.amount" })}${formCurrency ? ` (${formCurrency})` : ""}`}
              value={form.amount}
              onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
              error={Boolean(amountError)}
              helperText={
                amountError ??
                (isEntryFeeType(form.type)
                  ? intl.formatMessage({
                      id: form.external_relationship_id
                        ? "gestion.financials.entryFeeHintTournament"
                        : "gestion.financials.entryFeeHintGlobal",
                    })
                  : undefined)
              }
              inputProps={{ min: 0, step: 0.01, "data-testid": "template-amount" }}
            />
            <ChargeTypeSelect
              required
              native
              size="medium"
              label={intl.formatMessage({ id: "gestion.financials.templateType" })}
              value={form.type}
              disabled={Boolean(editingTemplate)}
              excludeTypes={["social-club-fee"]}
              onChange={(event) => {
                const nextType = event.target.value;
                setForm((prev) => ({
                  ...prev,
                  type: nextType,
                  name: isEntryFeeType(nextType)
                    ? intl.formatMessage({ id: "gestion.financials.chargeType.tournamentEntryFee" })
                    : prev.name,
                }));
              }}
            />
            <TextField
              fullWidth
              disabled={Boolean(editingTemplate)}
              label={intl.formatMessage({ id: "gestion.financials.sku" })}
              value={form.sku}
              onChange={(event) => setForm((prev) => ({ ...prev, sku: event.target.value }))}
              inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={intl.formatMessage({ id: "league.tribunal.comments" })}
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              error={Boolean(descriptionErrorId(form.description))}
              helperText={descriptionHelperText(intl, form.description)}
              inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{intl.formatMessage({ id: "league.planillero.cancel" })}</Button>
          <Button variant="contained" onClick={handleSaveTemplate} disabled={saving || !canSaveTemplate}>
            {intl.formatMessage({ id: "league.planillero.save" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default GestionFinancialPriceList;
